import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/shared/api/rest/client'
import { ENDPOINTS } from '@/shared/api/rest/config'
import { BranchesService } from '@/shared/api/rest/services/branches.service'
import { BranchRecord, ParentRecord } from '@/shared/api/rest/types'

/** The backend caps `limit` at 100 (`normalizePagination`'s `maxLimit`). */
const PAGE_LIMIT = 100

/**
 * Stop after this many pages. Ten sequential requests is already generous; the
 * point of the cap is that a directory of unexpected size degrades to "some
 * names missing" instead of hammering the API.
 */
const MAX_PAGES = 10

type ParentListRow = ParentRecord & { email?: string | null }

type ParentsListResponse = {
    data: ParentListRow[]
    meta?: { hasNextPage?: boolean; totalPages?: number; currentPage?: number }
}

export type DirectoryEntry = {
    _id: string
    firstName?: string | null
    lastName?: string | null
    email?: string | null
    phoneNumber?: string | null
    branchName?: string
    childCount: number
}

export type ParentDirectory = Record<string, DirectoryEntry | undefined>

/**
 * A name/email/phone lookup covering every parent, keyed by parent id.
 *
 * Built from `GET /parents?page=&limit=100`, which bulk-enriches each page from
 * `public.users` in a single join — the same source the parent registry list
 * uses. This exists because the duplicates endpoints hand back raw `parents`
 * rows with no name on them, and resolving those one id at a time does not
 * scale: a backlog of 70 pairs is 140 `GET /parents/:id` calls fired at once,
 * and the backend's JWT verification rate-limits its JWKS fetches to 10 per
 * minute, so most of that burst simply fails.
 *
 * Pages are fetched in sequence inside one query, so this is a trickle rather
 * than a burst, and the whole directory lands in one cache entry.
 */
export const useParentDirectory = (options: { enabled?: boolean } = {}) => {
    const query = useQuery({
        queryKey: ['parent-directory'],
        enabled: options.enabled ?? true,
        staleTime: 5 * 60_000,
        queryFn: async () => {
            const branches = await BranchesService.list().catch(
                () => [] as BranchRecord[]
            )
            const branchName = (branchId?: string | null) =>
                branchId ? branches.find((b) => b._id === branchId)?.name ?? '' : ''

            const rows: ParentListRow[] = []
            let truncated = false

            for (let page = 1; page <= MAX_PAGES; page += 1) {
                const response = await api.get<ParentsListResponse>(
                    ENDPOINTS.PARENTS.LIST({ page, limit: PAGE_LIMIT })
                )
                const data = response.data ?? []
                rows.push(...data)

                const meta = response.meta
                const hasNext = meta?.hasNextPage ?? data.length === PAGE_LIMIT
                if (!hasNext) break
                if (page === MAX_PAGES) truncated = true
            }

            const directory: ParentDirectory = {}
            for (const row of rows) {
                directory[row._id] = {
                    _id: row._id,
                    firstName: row.firstName,
                    lastName: row.lastName,
                    email: row.email,
                    phoneNumber: row.phoneNumber,
                    branchName: branchName(row.branchId),
                    // `GET /parents` left-joins children, so the count is real
                    // even though the full child records aren't used here.
                    childCount: row.children?.length ?? 0
                }
            }

            return { directory, truncated }
        }
    })

    return useMemo(
        () => ({
            directory: query.data?.directory ?? {},
            /** True when the page cap was hit, so some names are missing. */
            truncated: query.data?.truncated ?? false,
            loading: query.isLoading,
            fetching: query.isFetching,
            error: query.error ?? null
        }),
        [query.data, query.isLoading, query.isFetching, query.error]
    )
}

export const directoryName = (entry?: DirectoryEntry): string =>
    [entry?.firstName, entry?.lastName].filter(Boolean).join(' ').trim() ||
    entry?.email?.trim() ||
    ''
